
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, UserPlus, RefreshCw, UserCheck } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AccessRequest } from "@/types";

interface CreateUserFromRequestProps {
  accessRequest: AccessRequest;
  onSuccess?: () => void;
}

export function CreateUserFromRequest({ accessRequest, onSuccess }: CreateUserFromRequestProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRetry, setShowRetry] = useState(false);

  const handleCreateUser = async () => {
    setIsCreating(true);
    setError(null);
    setShowRetry(false);
    setIsSuccess(false);
    
    try {
      console.log("Creating user from request:", accessRequest);
      
      // Check if a user with this email already exists
      const { data: existingUsers, error: existingError } = await supabase
        .from('user_profiles')
        .select('id, email, is_approved')
        .eq('email', accessRequest.email);
        
      if (existingError) {
        console.error("Error checking existing users:", existingError);
        throw existingError;
      }
      
      if (existingUsers && existingUsers.length > 0) {
        console.log("User profile already exists:", existingUsers[0]);

        // Update the user profile to be approved
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ is_approved: true })
          .eq('id', existingUsers[0].id);

        if (updateError) {
          console.error("Error updating user profile:", updateError);
          throw updateError;
        }
        
        setIsSuccess(true);
        toast.success(`User with email ${accessRequest.email} already exists and has been approved`);
        
        // Update the access request status to approved
        const { error: requestUpdateError } = await supabase
          .from('access_requests')
          .update({ status: 'approved' })
          .eq('id', accessRequest.id);
          
        if (requestUpdateError) {
          console.error("Error updating access request:", requestUpdateError);
        }
          
        if (onSuccess) onSuccess();
        return;
      }
      
      console.log("Calling create-user edge function");
      
      // Generate a temporary random password - user will reset it later
      const temporaryPassword = Math.random().toString(36).slice(-12) + Math.random().toString(36).slice(-12);
      
      // Call our edge function to create the user with proper data mapping
      const { data, error } = await supabase.functions.invoke("create-user", {
        body: {
          email: accessRequest.email,
          password: temporaryPassword,
          firstName: accessRequest.firstName,
          lastName: accessRequest.lastName,
          phone: accessRequest.phone,
          companyName: accessRequest.companyName,
          licenseNumber: accessRequest.licenseNumber,
          role: 'customer', // Default role
          isApproved: true
        }
      });
      
      console.log("Edge function response:", data, error);
      
      if (error) {
        // If the error is about the user already existing, we can treat this as a success
        if (error.message?.includes("already been registered")) {
          console.log("User already exists in auth system, marking as successful");
          setIsSuccess(true);
          toast.success(
            `User account for ${accessRequest.firstName} ${accessRequest.lastName} already exists and has been approved.`
          );
          
          // Update the access request status to approved
          const { error: requestUpdateError } = await supabase
            .from('access_requests')
            .update({ status: 'approved' })
            .eq('id', accessRequest.id);
            
          if (requestUpdateError) {
            console.error("Error updating access request:", requestUpdateError);
          }
            
          if (onSuccess) onSuccess();
          return;
        }
        
        console.error("Edge function error:", error);
        throw error;
      }
      
      if (data?.success) {
        console.log("User created successfully");
        setIsSuccess(true);
        
        // Update the access request status to approved
        const { error: requestUpdateError } = await supabase
          .from('access_requests')
          .update({ status: 'approved' })
          .eq('id', accessRequest.id);
        
        if (requestUpdateError) {
          console.error("Error updating access request:", requestUpdateError);
        }
        
        toast.success(
          `User account for ${accessRequest.firstName} ${accessRequest.lastName} has been created successfully.`
        );
        
        if (onSuccess) onSuccess();
      } else if (data?.error) {
        console.error("Error from edge function:", data.error);
        throw new Error(data.error);
      } else {
        throw new Error("Failed to create user account - no success confirmation received");
      }
      
    } catch (err: any) {
      console.error("Error creating user:", err);
      setError(err.message || "An unexpected error occurred");
      toast.error(`Failed to create user: ${err.message}`);
      setShowRetry(true);
    } finally {
      setIsCreating(false);
    }
  };

  const retryCreation = () => {
    setShowRetry(false);
    handleCreateUser();
  };

  return (
    <div className="space-y-3">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            <div className="flex flex-col gap-2">
              <p>Error: {error}</p>
              <p className="text-xs">
                This could be due to network issues or problems with the authentication service. 
                You can try again or contact the administrator.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      {isSuccess ? (
        <Button 
          variant="default" 
          className="w-full"
          disabled
        >
          <UserCheck className="h-4 w-4 mr-2" />
          User Account Created
        </Button>
      ) : (
        <Button 
          variant="default" 
          className="w-full"
          onClick={handleCreateUser}
          disabled={isCreating}
        >
          {isCreating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Creating User...
            </>
          ) : (
            <>
              <UserPlus className="h-4 w-4 mr-2" />
              Create User Account
            </>
          )}
        </Button>
      )}
      
      {showRetry && (
        <Button 
          variant="outline" 
          className="w-full mt-2"
          onClick={retryCreation}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      )}
    </div>
  );
}
